/**
 * Payroll nav slugs (`payroll.*`) → the payroll `module.*` keys that grant them, ANY-of.
 * Shared so the shell rail and the payroll remote resolve a slug identically — the
 * shell cannot import the remote. Keep in sync with payroll's `P` catalog and
 * PAYROLL_PERMISSIONS; apps/payroll's shell-permissions.test guards both.
 */

// Setup & Configuration pages live inside the Settings panel, so both the Settings
// icon slug and `payroll.statutory.view` derive from them.
const SETUP_MODULE_KEYS = [
  'module.payroll.pay_schedule.view',
  'module.organization.statutory.view',
  'module.payroll.salary_component.view',
  'module.payroll.ot_lop.view',
  'module.payroll.ctc_template.view',
] as const;

export const PAYROLL_NAV_PERMISSION_ALIASES: Record<string, readonly string[]> = {
  'payroll.users.view': ['module.users.view'],
  'payroll.roles.manage': ['module.users.manage.roles'],
  // Settings icon: shown when ANY page in its panel is reachable.
  'payroll.organization.view': [
    'module.organization.branch.view',
    'module.users.manage.roles',
    'module.organization.bank_account.view',
    'module.payroll.notification_config.view',
    ...SETUP_MODULE_KEYS,
  ],
  'payroll.branch.view': ['module.organization.branch.view'],
  'payroll.bank.view': ['module.organization.bank_account.view'],
  'payroll.notifications.manage': ['module.payroll.notification_config.view'],
  'payroll.designation.view': ['module.organization.designation.view'],
  'payroll.department.view': ['module.organization.department.view'],
  'payroll.statutory.view': [...SETUP_MODULE_KEYS],
  'payroll.pay_schedule.view': ['module.payroll.pay_schedule.view'],
  'payroll.salary_component.view': ['module.payroll.salary_component.view'],
  'payroll.ctc_template.view': ['module.payroll.ctc_template.view'],
  'payroll.calculator.view': ['module.payroll.calculator.view'],
  'payroll.attendance.view': ['module.payroll.attendance.view'],
  'payroll.payroll_run.view': ['module.payroll.run.view'],
  'payroll.run.out_of_cycle': ['module.payroll.run.out_of_cycle'],
  'payroll.run.simulate': ['module.payroll.run.simulate'],
  // Variable Pay icon → Reimbursements + Loans panel (self or admin view).
  'payroll.reimbursement.self': [
    'module.payroll.reimbursement.self',
    'module.payroll.reimbursement.view',
    'module.payroll.loan.self',
    'module.payroll.loan.view',
  ],
  'payroll.loan.self': ['module.payroll.loan.self', 'module.payroll.loan.view'],
  // Records icon → My Payslips / Past Runs / TDS / Form 16 panel.
  'payroll.payslip.self.view': [
    'module.payroll.payslip.view.own',
    'module.payroll.run.view',
    'module.payroll.tds.report.view',
    'module.payroll.tax.form.manage',
    'module.payroll.tax.form.view.own',
  ],
  'payroll.tds_report.view': ['module.payroll.tds.report.view'],
  'payroll.tax_form.self.view': ['module.payroll.tax.form.view.own'],
};

/** Every payroll nav slug as an explicit boolean, resolved from a `module.*` grant map. */
export function resolvePayrollNavPermissions(
  granted: Record<string, boolean>,
): Record<string, boolean> {
  const slugs: Record<string, boolean> = {};
  for (const [slug, moduleKeys] of Object.entries(PAYROLL_NAV_PERMISSION_ALIASES)) {
    slugs[slug] = moduleKeys.some((k) => granted[k] === true);
  }
  return slugs;
}
